import {
  Modal as MantineModal,
  ModalCloseButtonProps,
  ModalContentProps,
  ModalRootProps,
  rem,
  ModalBaseStylesNames,
  ScrollArea,
  ModalBaseProps
} from '@mantine/core';
import cx from 'clsx';
import { CSSProperties, ReactNode, Ref } from 'react';

import classes from './Modal.module.scss';
import { HEADER_HEIGHT_PX } from '../../../client/consts/styleConsts';
import { MODAL_PORTAL_ID } from '../../../utils/consts';

type Modal = {
  opened: boolean;
  children: ReactNode;
  aboveLoadingOverlay?: boolean;
  title?: string;
  dataTestPrefix?: string;
  modalContentProps?: ModalContentProps;
  contentRef?: Ref<HTMLDivElement>;
  closeButtonProps?: ModalCloseButtonProps;
  overridingClasses?: Partial<Record<ModalBaseStylesNames, string>>;
  styles?: Partial<Record<ModalBaseStylesNames, CSSProperties>>;
  size?: ModalBaseProps['size'];
  withOverlay?: boolean;
} & ModalRootProps;

export function Modal(props: Modal) {
  const {
    aboveLoadingOverlay,
    title,
    children,
    dataTestPrefix = 'modal',
    modalContentProps,
    contentRef,
    closeButtonProps,
    overridingClasses,
    styles,
    size = 'xl',
    withOverlay = true,
    classNames,
    ...modalRootProps
  } = props;

  // A consumer `classNames` replaces the defaults wholesale (long-standing contract — consumers
  // style every slot they care about). Only the viewport-inset classes are merged unconditionally:
  // staying clear of whatever occludes the viewport's bottom is the modal's own concern.
  const slotClasses = classNames ?? {
    header: classes.modalHeader,
    inner: classes.modalInner,
    content: classes.modalContent,
    close: classes.closeButton,
    title: classes.title,
    ...overridingClasses
  };

  return (
    <MantineModal.Root
      classNames={{
        ...slotClasses,
        inner: cx(slotClasses.inner, classes.viewportInsetInner, aboveLoadingOverlay && classes.aboveLoadingOverlay),
        content: cx(slotClasses.content, classes.viewportInsetContent),
        overlay: cx(slotClasses.overlay, aboveLoadingOverlay && classes.aboveLoadingOverlay)
      }}
      styles={styles}
      scrollAreaComponent={ScrollArea.Autosize}
      yOffset={rem(HEADER_HEIGHT_PX)}
      size={size}
      centered
      {...modalRootProps}
    >
      {withOverlay && <MantineModal.Overlay />}
      <MantineModal.Content id={MODAL_PORTAL_ID} ref={contentRef} {...modalContentProps}>
        <MantineModal.Header>
          <MantineModal.Title>{title}</MantineModal.Title>
          <MantineModal.CloseButton
            variant="transparent"
            color="accent"
            data-testid={`${dataTestPrefix}-close`}
            {...closeButtonProps}
          />
        </MantineModal.Header>
        <MantineModal.Body data-testid={`${dataTestPrefix}-content`}>{children}</MantineModal.Body>
      </MantineModal.Content>
    </MantineModal.Root>
  );
}
